<script>
import lazyLoadComponent from '@router/lazyload-component'
import { debounce } from 'lodash'
import {
  orderPaymentMethodMap,
  orderPaymentMethodFilter,
  paymentStatusMapFilter,
} from '@utils/filter-value-to-text'
import { formatRequestData } from '@utils/format-date'
import { formatHotelOrderStayRange } from '@utils/format-hotel-stay'
import HotelOrderStatusTag from '@/src/router/views/schedule/hotel/components/order/partials/HotelOrderStatusTag.vue'
import pageMixin from '@src/mixins/pagination'
import { getHotelInfo } from '@/src/router/views/hotel/api'
import { mapGetters, mapMutations } from 'vuex'

export default {
  page: {
    title: '訂單查詢',
    meta: [{ name: 'description', content: 'Hotel Order List' }],
  },
  components: {
    BaseStateLabel: () => lazyLoadComponent(import('@components/BaseStateLabel')),
    HotelOrderStatusTag,
  },
  filters: {
    orderPaymentMethodFilter,
    paymentStatusMapFilter,
  },
  mixins: [pageMixin],
  data() {
    return {
      filterData: {
        memberName: '',
        memberPhone: '',
        orderDisplayId: '',
        orderStatus: '',
        paymentMethod: 'All',
        startDate: '',
        endDate: '',
      },
      paymentMethodList: Object.freeze(this.reduceMethod({ ...orderPaymentMethodMap })),
      pickerOptions: {
        shortcuts: [
          {
            text: '最近一週',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
              picker.$emit('pick', [start, end])
            },
          },
          {
            text: '最近一個月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            },
          },
          {
            text: '最近三個月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            },
          },
        ],
      },
      loadedOrders: [],
      tryingToGetOrder: false,
      tryingToExport: false,
      isFilterChange: false,
      expandRowKeys: [],
      pageLimit: 15,
    }
  },
  computed: {
    ...mapGetters('hotel/setting', ['showCheckoutDate']),
    filterDateRange: {
      get() {
        return [this.filterData.startDate, this.filterData.endDate]
      },
      set(value) {
        const [start, end] = value || ['', '']
        this.filterData.startDate = start
        this.filterData.endDate = end
      },
    },
    fetchDataParams() {
      const { startDate, endDate, ...filterDataWithoutDate } = this.filterData
      const searchWithDate = startDate && endDate

      let searchParams = { ...filterDataWithoutDate }

      if (searchWithDate) {
        searchParams.startDate = formatRequestData(startDate)
        searchParams.endDate = formatRequestData(endDate)
      }

      if (searchParams.paymentMethod === 'All') {
        delete searchParams.paymentMethod
      }

      const pageQuery = {
        page: this.currentPage,
        // 後端 DataFilter 只認 perPage，不認 limit（見 vendor dddream DataFilter::applyAndPaginate）
        perPage: this.pageLimit,
        sortBy: 'createdAt',
        sortDirection: 'desc',
      }

      return {
        ...searchParams,
        ...pageQuery,
      }
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToFetchList',
    },
    filterData: {
      handler() {
        this.isFilterChange = true
      },
      deep: true,
    },
  },
  async mounted() {
    await this.fetchHotelSetting()
    this.tryToFetchList()
  },
  methods: {
    ...mapMutations('hotel/setting', ['SET_SHOW_CHECKOUT_DATE']),
    async fetchHotelSetting() {
      try {
        const hotelInfo = await getHotelInfo()
        this.SET_SHOW_CHECKOUT_DATE(hotelInfo?.showCheckoutDate)
      } catch (error) {
        console.error('取得住宿設定失敗:', error)
        this.SET_SHOW_CHECKOUT_DATE(1)
      }
    },
    reduceMethod(list) {
      if (!this.$hasDeposit()) {
        delete list.Deposit
      }
      if (!this.$cashFlowSettingPass()) {
        delete list.Vacc
        delete list.Credit
      }
      return { ...list }
    },
    async handleExport() {
      if (this.tryingToExport) return
      this.tryingToExport = true
      try {
        const { fileName, csvData } = await this.$store.dispatch('hotel/order/export', this.fetchDataParams)
        const csvFormatData = csvData.map((row) => Object.values(row).join(',')).join('\n')

        const blob = new Blob(['\uFEFF', csvFormatData], {
          type: 'text/csv;charset=utf-8;',
        })

        const url = window.URL.createObjectURL(blob)

        const tempLink = document.createElement('a')
        tempLink.download = fileName
        tempLink.href = url
        tempLink.click()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error(error)
      } finally {
        this.tryingToExport = false
      }
    },
    async tryToFetchListFilter() {
      // 當篩選前不在第一頁的時候，跳回第一頁
      if (this.currentPage !== 1) {
        this.currentPage = 1
        return
      }
      await this.tryToFetchList()
      this.isFilterChange = false
    },
    currentPageLimit() {
      try {
        const remainder = this.$refs.pagination.total % this.$refs.pagination.pageSize
        const qotient = this.$refs.pagination.total / this.$refs.pagination.pageSize
        const totalPage = remainder ? parseInt(qotient, 10) + 1 : parseInt(qotient, 10)
        // total=0 時 totalPage=0，不可把 currentPage 設成 0（會觸發 page=0 → INPUT_INVALID）
        if (this.currentPage > totalPage && totalPage > 0) {
          this.currentPage = totalPage
        }
      } catch (error) {
        console.error(error.message)
      }
    },
    normalizeHotelOrderItems(response) {
      const raw = response.items ?? response.data
      if (Array.isArray(raw)) {
        return raw.slice()
      }
      if (raw && Array.isArray(raw.items)) {
        return raw.items.slice()
      }
      return []
    },
    async tryToFetchList() {
      try {
        const response = await this.$store.dispatch('hotel/order/fetchList', this.fetchDataParams)
        this.loadedOrders = this.normalizeHotelOrderItems(response)
        const pagination = response.pagination || {}
        this.$nextTick(() => {
          // Element UI 會在曾排序過的表格上保留 sortingColumn，execSort 會對 :data 再做 orderBy，
          // 造成「API items 順序正確、畫面卻被前端重排」。每次載入後清掉排序狀態。
          const table = this.$refs.hotelOrderTable
          if (table && typeof table.clearSort === 'function') {
            table.clearSort()
          }
          if (this.$refs.pagination) {
            this.$refs.pagination.updateTotal(pagination.total || 0)
            this.pageLimit = Number(pagination.perPage) || 15
            this.currentPageLimit()
          }
        })
      } catch (error) {
        console.error(error.message)
      }
    },
    clearSearchQuery() {
      this.$router.push(this.$route.path)
    },
    onCellClick(row, column) {
      if (column.type === 'expand') return

      this.$router.push({
        name: 'hotelViewOrder',
        params: { productId: row.productId, containerId: row.containerId },
      })
    },
    handleExpand(row) {
      if (this.expandRowKeys[0] === row.containerId) {
        this.expandRowKeys = []
      } else {
        this.expandRowKeys = [row.containerId]
      }
    },
    getRowKeys(row) {
      return row.containerId
    },
    tryToSearchByInput: debounce(function() {
      this.tryToFetchList()
    }, 300),
    formatBookingDates(order) {
      return formatHotelOrderStayRange(order, this.showCheckoutDate)
    },
    getRoomTitles(bookings) {
      if (!bookings || bookings.length === 0) return ''
      const titles = [...new Set(bookings.map((b) => b.title))]
      return titles.join('、')
    },
    isOrderRefunded(row) {
      return Array.isArray(row.orders) && row.orders.some((o) => o.isRefunded === true)
    },
  },
}
</script>

<template>
  <div>
    <div :class="$style.btnTop" class="btn-top-space-between">
      <div class="side search">
        <el-input v-model.trim.lazy="filterData.orderDisplayId" clearable placeholder="輸入訂單編號" @input="tryToSearchByInput">
          <BaseIcon slot="suffix" name="search" class="el-input__icon" />
        </el-input>
      </div>
      <div class="side btn ml-md-3">
        <el-button class="is-round" plain @click="handleExport"> <BaseIcon name="file-export" /> 訂單匯出 </el-button>
      </div>
    </div>
    <BaseTableSearch v-slot="{ fields }" class="isForTab" hide-action :default-value.sync="filterData" :class="$style.filter" @reset="tryToFetchList" @tryToFetchList="tryToFetchListFilter">
      <el-form-item prop="memberName" style="width: 16%">
        <el-input v-model="fields.memberName" placeholder="使用人姓名" clearable @input="tryToSearchByInput" />
      </el-form-item>
      <el-form-item prop="memberPhone" style="width: 16%">
        <el-input v-model="fields.memberPhone" placeholder="手機號碼" clearable @input="tryToSearchByInput" />
      </el-form-item>
      <el-form-item prop="orderStatus" style="width: 16%">
        <el-select v-model="fields.orderStatus" placeholder="選擇訂單狀態" clearable @change="tryToFetchListFilter">
          <el-option label="所有狀態" value></el-option>
          <el-option label="已成立" value="1"></el-option>
          <el-option label="已取消" value="0"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="paymentMethod" style="width: 16%">
        <el-select v-model="fields.paymentMethod" placeholder="選擇付款方式" @change="tryToFetchListFilter">
          <el-option label="所有付款方式" value="All"></el-option>
          <el-option v-for="(label, value) in paymentMethodList" :key="value" :label="label" :value="value"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item style="width: 36%">
        <el-date-picker
          v-model="filterDateRange"
          type="daterange"
          align="right"
          unlink-panels
          range-separator="至"
          start-placeholder="訂單起始日期"
          end-placeholder="訂單結束日期"
          :picker-options="pickerOptions"
          @change="tryToFetchListFilter"
        ></el-date-picker>
      </el-form-item>
    </BaseTableSearch>
    <div class="isStylePageContent">
      <div class="el-table-wrapper">
        <el-table
          ref="hotelOrderTable"
          v-loading="tryingToGetOrder"
          element-loading-text="取得訂單資訊中"
          :data="loadedOrders"
          :row-key="getRowKeys"
          :expand-row-keys="expandRowKeys"
          @cell-click="onCellClick"
          @expand-change="handleExpand"
        >
          <el-table-column v-slot="{ row }" type="expand">
            <div :class="$style['order-expanded']">
              <div v-for="(order, index) in row.orders" :key="index" class="expanded-item">
                <div class="item">
                  <div>
                    <div class="Jost fs-sm fw-medium">{{ `#${order.displayId}` }}</div>
                    <div class="mt-1">
                      <HotelOrderStatusTag :status="order.status" />
                    </div>
                  </div>
                  <div>
                    <div>預約日期：{{ formatBookingDates(order) }}</div>
                    <div class="fs-2sm text-secondary-light">{{ getRoomTitles(order.bookings) }}</div>
                  </div>
                  <div>
                    <div class="Jost">${{ order.total }}</div>
                  </div>
                </div>
                <el-divider v-if="index < row.orders.length - 1"></el-divider>
              </div>
            </div>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="訂單編號" prop="displayId" min-width="150">
            <div class="Jost fs-sm fw-medium">{{ `#${row.displayId}` }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="使用人資訊" min-width="180">
            <div>{{ row.memberName }}</div>
            <div class="fs-2sm text-secondary-light">{{ row.memberPhone }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="付款方式" prop="paymentMethod" min-width="150">
            <BaseStateLabel state="secondary-light" text>
              {{ row.paymentMethod | orderPaymentMethodFilter }}
            </BaseStateLabel>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="付款狀態" prop="paymentStatus" min-width="130">
            <BaseStateLabel :state="row.paymentStatus | paymentStatusMapFilter('color')" text>
              {{ row.paymentStatus | paymentStatusMapFilter('text') }}
              <span v-if="isOrderRefunded(row)" style="color: #E6A23C">(已退款)</span>
            </BaseStateLabel>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="總金額" min-width="100">
            <div class="Jost">${{ row.total }}</div>
          </el-table-column>
          <el-table-column min-width="40">
            <el-tooltip content="查看訂單詳情" placement="top">
              <el-button type="text">
                <BaseIcon name="ellipsis-v" />
              </el-button>
            </el-tooltip>
          </el-table-column>
        </el-table>
      </div>
      <BasePagination ref="pagination" :page-size="pageLimit" :current-page.sync="currentPage" />
    </div>
  </div>
</template>

<style lang="scss" module>
:global {
  :local(.filter) {
    & > div {
      flex-wrap: wrap;
      .el-form-item {
        @include max-lg {
          width: 100% !important;
        }
      }

      & + button {
        align-self: baseline;
      }
    }
  }
}

.order-expanded {
  padding: 20px;
  :global {
    .expanded-item {
      .item {
        display: flex;
        align-items: center;
        padding: 10px 0;

        > div {
          flex: 1;
          &:first-child {
            flex-basis: 25%;
          }
          &:nth-child(2) {
            flex-basis: 50%;
          }
          &:last-child {
            flex-basis: 25%;
            text-align: right;
          }
        }
      }
    }
  }
}

.btnTop {
  padding: 0.5rem 0;

  @include max-sm {
    display: flex;
    flex-direction: column;
  }

  :global {
    .side {
      @include max-sm {
        display: flex;
      }

      &:first-child {
        @include max-sm {
          margin-bottom: 0.5rem;

          .el-button {
            flex: 1 0 0;
            width: 50%;
          }
        }
      }

      .el-input {
        @include max-sm {
          margin-left: 0 !important;
          width: 100%;
        }
      }
    }
  }
}
</style>
